import {
	Alert, Avatar,
	Box,
	Button,
	Fade,
	TextField, useTheme
} from "@mui/material"
import React, { useEffect, useRef, useState } from "react"
import { doGet, doPost } from "../../util/do-fetch"
import Message from "../prepkit/chat/Message"
import { ArrowForward } from "@mui/icons-material"
import Markdown from "react-markdown"
import LeftRight from "../LeftRight"
import TextFormatter from "../TextFormatter"

const Welcome = ({ prepKit, user, userProfile, onComplete }) => {
	const [firstName, setFirstName] = useState(userProfile?.first_name || '')
	const [lastName, setLastName] = useState(userProfile?.last_name || '')
	const [aiPersonality, setAiPersonality] = useState(userProfile?.ai_personality_id)
	const [aiPersonalities, setAiPersonalities] = useState([])
	const [step, setStep] = useState(0)
	const [error, setError] = useState(null)
	const containerRef = useRef(null)

	useEffect(() => {
		const fetchAiPersonalities = async () => {
			try {
				const response = await doGet(`/mini/ais`)
				const data = await response.json()
				setAiPersonalities(data)
			} catch (err) {
				console.error(err)
			}
		}
		fetchAiPersonalities()
	}, [])

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
		}
	}, [step])

	const handleWelcomeComplete = async () => {
		try {
			const options = {
				body: JSON.stringify({
					first_name: firstName,
					last_name: lastName,
					ai_personality_id: aiPersonality,
					welcomed: true
				})
			}
			const response = await doPost(`/mini/user/profile`, options)
			if (response.ok) {
				const profile = await response.json()
				onComplete(profile)
			} else {
				setError("Error saving user profile.")
			}
		} catch {
			setError("Error saving user profile.")
		}
	}

	const selectedPersonalityName = aiPersonalities.find(p => p.id === aiPersonality)?.name

	return (
		<Box ref={containerRef} textAlign="left" pb={10}>
			{error && (
				<Alert color="error" onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{step >= 0 && (
				<TypeMessage
					key="intro"
					text={`Hello and welcome to your ${prepKit?.name || ''} PrepKit! 🎉`}
					onTypingComplete={() => setStep(1)}
				/>
			)}

			{step >= 1 && (
				<TypeMessage
					key="ask-name"
					text={`I'm Birdsy, your personal AI Study Partner. What’s your first and last name?`}
					onTypingComplete={() => setStep(2)}
				/>
			)}

			{step === 2 && (
				<Fade in timeout={2000}>
					<Box mx={11} mt={3}>
						<Box display="flex" gap={2}>
							<TextField
								label="First Name"
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								fullWidth
								autoFocus
							/>
							<TextField
								label="Last Name"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								fullWidth
							/>
						</Box>
						<Button variant="outlined" size="large" sx={{ mt: 1 }} onClick={() => setStep(3)}>
							Confirm Name
						</Button>
					</Box>
				</Fade>
			)}

			{step >= 3 && (
				<TypeMessage
					key="name-confirm"
					text={`My name is ${firstName} ${lastName}`}
					isUser={true}
					onTypingComplete={() => setStep(4)}
				/>
			)}

			{step >= 4 && aiPersonalities.length > 0 && (
				<Box>
					<TypeMessage
						key="ask-style"
						text={`Thanks, ${firstName}! Everyone learns differently — which style fits you best?\n\n${aiPersonalities.map(p => `**${p.name}**: ${p.description}`).join('\n\n')}`}
						onTypingComplete={() => setStep(5)}
					/>
				</Box>
			)}

			{step === 5 && aiPersonalities.length > 0 && (
				<Fade in timeout={1000}>
					<Box mx={11} mt={2} gap={2} display={"flex"} justifyContent={"flex-start"}>
						{aiPersonalities.map(p => (
							<Button
								key={p.id}
								onClick={() => {
									setAiPersonality(p.id)
									setStep(6)
								}}
								variant="outlined"
								size="large"
								sx={{mr: 1}}
							>
								{p.name}
							</Button>
						))}
					</Box>
				</Fade>
			)}

			{step >= 6 && selectedPersonalityName && (
				<TypeMessage
					key="style-confirm"
					text={`I prefer ${selectedPersonalityName}.`}
					isUser={true}
					onTypingComplete={() => setStep(7)}
				/>
			)}

			{step >= 7 && (
				<TypeMessage
					key="wrap"
					text={`Got it, ${firstName}. I’ll make sure to keep our sessions **${selectedPersonalityName}** moving forward!\n\nNow that we’re all set, let’s dive into your exam prep journey. Ready to jump in?`}
					onTypingComplete={() => setStep(8)}
				/>
			)}

			{step === 8 && (
				<Fade in timeout={1000}>
					<Box mx={5} mt={2} display="flex" justifyContent="center">
						<Button
							onClick={handleWelcomeComplete}
							variant="contained"
							size="large"
							sx={{ px: 5, py: 2 }}
						>
							Let's Get Started! <ArrowForward />
						</Button>
					</Box>
				</Fade>
			)}
		</Box>
	)
}

const TypeMessage = ({ text, isUser, onTypingComplete }) => {
	const theme = useTheme()
	const [displayed, setDisplayed] = useState('')
	const [complete, setComplete] = useState(false)
	const indexRef = useRef(0)

	const handleComplete = () => {
		setComplete(true)
		if (onTypingComplete) onTypingComplete()
	}

	useEffect(() => {
		const interval = setInterval(() => {
			setDisplayed(prev => {
				const next = text.slice(0, indexRef.current + 4)
				indexRef.current++
				if (indexRef.current >= text.length) {
					clearInterval(interval)
					handleComplete()
				}
				return next
			})
		}, 15) // typing speed

		return () => clearInterval(interval)
	}, [text])

	return (
		isUser
			?   <Box mx={5}
			         my={2}
			         fontSize="1.1rem"
			         whiteSpace="pre-wrap"
			         display="flex"
			         flexDirection="row"
			         justifyContent="flex-end"
			         alignItems="flex-start"
				>
					<Box sx={{
						px: 2,
						backgroundColor: theme.palette.background.default,
						borderRadius: '10px',
						borderBottomRightRadius: 0,
					}}>
						<TextFormatter text={displayed}/>
					</Box>
				</Box>
			:	<Box mx={5}
			         my={2}
			         display="flex"
			         flexDirection="row"
			         justifyContent="flex-start"
			         alignItems="flex-start"
				>
					<Avatar src="/birdsy-avatar-black.png"
					        sx={{
						        border: '1px solid ' + theme.palette.divider,
						        padding: '2px',
						        width: 30,
						        height: 30,
						        mr: 2,
						        ml: 0,
						        mt: 2,
						        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)'
					        }}
					/>
					<Box fontSize="1.1rem"
					     whiteSpace="pre-wrap"
					     sx={{
						     '& p': {
							     display: 'inline-block',
							     marginBottom: '0px',
						     }
					     }}
					>
						<Markdown>{displayed + (complete ? '' : ' **|**')}</Markdown>
					</Box>
				</Box>
	)
}


export default Welcome
